import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faCheckCircle, faExclamationTriangle,
  faUser, faGraduationCap, faCalendarAlt, faSync, faTimes,
  faSearch, faFileCsv, faFilter, faSpinner, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { InscriptionContext } from '../layout/CunstomInscription';

const API_INSCRIPTION = `${API_CONFIG2.apiUrl}inscription/api_inscriptionX.php`;

// ---- Cache Dexie ----
const db = new Dexie('ManageInscriptionsCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000;

// ---- Dédoublonnage des requêtes en cours ----
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageInscriptions = ({ userId, fonction }) => {
  const context = useContext(InscriptionContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [dataSource, setDataSource] = useState('cache');

  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterFormation, setFilterFormation] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10
  });

  const [clients, setClients] = useState([]);
  const [formations, setFormations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    idcl: '',
    id_formation: '',
    id_session: ''
  });

  const searchTimeout = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // Toast helper
  const showToast = useCallback((message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
  }, []);

  // ============================================================
  // CACHE HELPERS
  // ============================================================
  const getCacheKey = (page, search, client, formation) =>
    `inscriptions_${page}_${search}_${client}_${formation}`;

  const loadFromCache = async (key) => {
    try {
      const cached = await db.cache.get(key);
      if (cached && cached.timestamp && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return cached.data;
      }
      return null;
    } catch { return null; }
  };

  const saveToCache = async (key, data) => {
    try {
      await db.cache.put({ key, data, timestamp: Date.now() });
    } catch { /* ignore */ }
  };

  const invalidateCache = async () => {
    try {
      const keys = await db.cache.toCollection().primaryKeys();
      const toDelete = keys.filter(k => k.startsWith('inscriptions_'));
      await db.cache.bulkDelete(toDelete);
    } catch { /* ignore */ }
  };

  // ============================================================
  // CHARGEMENT DES DONNÉES (avec dédoublonnage + annulation)
  // ============================================================
  const fetchData = useCallback(async (page = 1, force = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = getCacheKey(page, search, filterClient, filterFormation);
    const requestKey = force ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est en cours, on l'attend
    if (pendingFetches.has(requestKey) && !force) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setInscriptions(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (force) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache (sauf force)
        if (!force) {
          const cached = await loadFromCache(cacheKey);
          if (cached && isMounted.current) {
            setInscriptions(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
        const params = new URLSearchParams({
          action: 'list',
          page,
          limit: pagination.limit,
          search,
          idcl: filterClient,
          id_formation: filterFormation
        });
        const url = `${API_INSCRIPTION}?${params}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await loadFromCache(cacheKey);
          if (stale && isMounted.current) {
            setInscriptions(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache périmé', 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchData(page, force, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await loadFromCache(cacheKey);
          if (stale && isMounted.current) {
            setInscriptions(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache`, 'warning');
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
        }

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setInscriptions(data.data);
            setPagination(data.pagination);
            const source = force ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await saveToCache(cacheKey, result);
            if (force) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur inconnue');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await loadFromCache(cacheKey);
        if (stale && isMounted.current) {
          setInscriptions(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(`Erreur : ${err.message}`, 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [search, filterClient, filterFormation, pagination.limit, showToast, setSource]);

  // ============================================================
  // CHARGEMENT DES DONNÉES COMPLÉMENTAIRES (sans dédoublonnage)
  // ============================================================
  const fetchClients = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API_INSCRIPTION}?action=clients`);
      if (data.status === 'success') setClients(data.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchFormations = useCallback(async () => {
    try {
      const data = await fetchJSON(`${API_INSCRIPTION}?action=formations`);
      if (data.status === 'success') setFormations(data.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchSessions = useCallback(async (idFormation) => {
    if (!idFormation) {
      setSessions([]);
      return;
    }
    setLoadingSessions(true);
    try {
      const data = await fetchJSON(`${API_INSCRIPTION}?action=sessions&id_formation=${idFormation}`);
      setSessions(data.status === 'success' ? data.data : []);
    } catch (err) {
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  // ============================================================
  // INITIALISATION ET EFFETS
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchData(1, false);
    fetchClients();
    fetchFormations();

    return () => {
      isMounted.current = false;
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Réaction au refresh global (depuis le contexte)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchData(pagination.currentPage, true);
    }
  }, [refreshKey, fetchData, pagination.currentPage]);

  // Filtres et recherche (avec debounce)
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchData(1, false);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [search, filterClient, filterFormation, fetchData]);

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    window.open(`${API_INSCRIPTION}?action=export`, '_blank');
  };

  // ============================================================
  // GESTION DES MODALES
  // ============================================================
  const openCreateModal = () => {
    setEditing(false);
    setFormData({ id: '', idcl: '', id_formation: '', id_session: '' });
    setSessions([]);
    setShowModal(true);
  };

  const openEditModal = (inscription) => {
    setEditing(true);
    setFormData({
      id: inscription.id,
      idcl: inscription.idcl,
      id_formation: inscription.id_formation,
      id_session: inscription.id_session
    });
    fetchSessions(inscription.id_formation);
    setShowModal(true);
  };

  const openDeleteModal = (inscription) => {
    setFormData({ ...inscription });
    setShowDeleteModal(true);
  };

  // ============================================================
  // GESTION DU FORMULAIRE
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'id_formation') {
      setFormData(prev => ({ ...prev, id_session: '' }));
      fetchSessions(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const action = editing ? 'update' : 'create';
      const method = editing ? 'PUT' : 'POST';
      const payload = editing
        ? { id: formData.id, idcl: formData.idcl, id_formation: formData.id_formation, id_session: formData.id_session }
        : { idcl: formData.idcl, id_formation: formData.id_formation, id_session: formData.id_session };

      const data = await fetchJSON(`${API_INSCRIPTION}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        await invalidateCache();
        fetchData(pagination.currentPage, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(`Erreur : ${err.message}`, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const data = await fetchJSON(`${API_INSCRIPTION}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowDeleteModal(false);
        await invalidateCache();
        fetchData(pagination.currentPage, true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(`Erreur : ${err.message}`, 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchData(page, false);
    }
  };

  // ============================================================
  // RENDU (inchangé, avec style)
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }
        .inscriptions-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 28px;
          padding: clamp(1rem, 2vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 28px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }
        .section-title {
          font-size: clamp(1rem, 1.4vw, 1.2rem);
          font-weight: 600;
          color: var(--accent-blue);
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 1.2rem;
        }
        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }
        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          color: var(--text-primary);
          padding: 10px 16px;
          width: 100%;
          transition: border-color 0.2s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }
        .monetra-btn {
          padding: 8px 16px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          cursor: pointer;
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 4px 16px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          border-color: #ef4444;
          color: #f87171;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.12);
        }
        .monetra-btn.success {
          border-color: #22c55e;
          color: #4ade80;
        }
        .monetra-btn.success:hover:not(:disabled) {
          background: rgba(34, 197, 94, 0.12);
        }
        .search-wrapper {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          align-items: center;
        }
        .search-wrapper .filter-group {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          flex: 1;
        }
        .search-wrapper .filter-group > * {
          min-width: 160px;
        }
        .search-wrapper .actions-group {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 14px 12px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 14px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }
        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 1.5rem;
          padding-top: 1.2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
        }
        .pagination-controls .buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .pagination-controls .buttons button {
          padding: 6px 14px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.2s;
          min-height: 36px;
        }
        .pagination-controls .buttons button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .pagination-controls .buttons button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-controls .buttons button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
        }
        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .toast-custom {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 20px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 12px 40px rgba(0,0,0,0.6) !important;
        }
        .badge-count {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
          padding: 2px 10px;
          border-radius: 30px;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
        }
        .text-secondary { color: var(--text-secondary); }
        .text-danger { color: #ef4444; }
        .text-success { color: var(--accent-blue); }
        .display-6 { font-size: clamp(1.6rem, 4vw, 2.2rem); font-weight: 700; }
        .fw-bold { font-weight: 600; }
        .d-flex { display: flex; }
        .justify-content-between { justify-content: space-between; }
        .align-items-center { align-items: center; }
        .flex-wrap { flex-wrap: wrap; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .ms-auto { margin-left: auto; }
        .py-4 { padding-top: 1.5rem; padding-bottom: 1.5rem; }
        .text-center { text-align: center; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }

        @media (max-width: 768px) {
          .inscriptions-page { padding: 12px; }
          .search-wrapper .filter-group > * { min-width: 100%; }
          .search-wrapper .actions-group { flex-wrap: wrap; }
          .glass-card { padding: 1rem; }
          .monetra-btn { font-size: 0.75rem; padding: 6px 12px; min-height: 38px; }
          .pagination-controls .buttons button { padding: 4px 10px; font-size: 0.7rem; min-height: 32px; }
          .glass-table th, .glass-table td { padding: 8px 6px; }
          .monetra-modal .modal-footer { flex-direction: column-reverse; }
          .monetra-modal .modal-footer button { width: 100%; justify-content: center; }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.7rem; padding: 4px 10px; min-height: 34px; }
        }
      `}</style>

      <div className="inscriptions-page">
        {/* En-tête avec badge source et bouton refresh */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold">Gestion des inscriptions</h1>
            <p className="text-secondary" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez, créez et gérez les inscriptions aux sessions de formation.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn primary" onClick={() => fetchData(pagination.currentPage, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSync} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn success" onClick={exportCSV}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
            <button className="monetra-btn primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="glass-card">
          <div className="search-wrapper">
            <div className="filter-group">
              <div style={{ flex: 2, minWidth: '200px' }}>
                <Form.Control
                  type="text"
                  placeholder="Rechercher (client, formation, mois...)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="monetra-input"
                />
              </div>
              <div style={{ minWidth: '160px' }}>
                <Form.Select
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                  className="monetra-input"
                >
                  <option value="">Tous les clients</option>
                  {clients.map(c => (
                    <option key={c.idcl} value={c.idcl}>{c.nom}</option>
                  ))}
                </Form.Select>
              </div>
              <div style={{ minWidth: '160px' }}>
                <Form.Select
                  value={filterFormation}
                  onChange={(e) => setFilterFormation(e.target.value)}
                  className="monetra-input"
                >
                  <option value="">Toutes les formations</option>
                  {formations.map(f => (
                    <option key={f.id} value={f.id}>{f.titre}</option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="glass-card">
          <div className="section-title">
            <FontAwesomeIcon icon={faGraduationCap} />
            Liste des inscriptions
            <span className="badge-count">{pagination.totalRecords} total</span>
          </div>

          {loading && !refreshing ? (
            <div className="text-center py-4"><Spinner variant="primary" /></div>
          ) : inscriptions.length === 0 ? (
            <p className="text-secondary">Aucune inscription trouvée.</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="glass-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Client</th>
                      <th>Formation</th>
                      <th>Session</th>
                      <th>Date d'inscription</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscriptions.map(ins => (
                      <tr key={ins.id}>
                        <td>#{ins.id}</td>
                        <td><FontAwesomeIcon icon={faUser} className="me-1" /> {ins.client_nom}</td>
                        <td><FontAwesomeIcon icon={faGraduationCap} className="me-1" /> {ins.formation_titre}</td>
                        <td><FontAwesomeIcon icon={faCalendarAlt} className="me-1" /> {ins.session_mois} (du {ins.session_debut} au {ins.session_fin})</td>
                        <td>{new Date(ins.date_inscription).toLocaleString()}</td>
                        <td>
                          {(fonction === 'SUPER ADMIN' || fonction === 'DSI' || fonction === 'COMPTABLE' || fonction === 'CAISSIER') && (
                            <>
                            <button className="monetra-btn me-1" onClick={() => openEditModal(ins)} disabled={loading || refreshing}>
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="monetra-btn danger" onClick={() => openDeleteModal(ins)} disabled={loading || refreshing}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          </>
                          )}
                         
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-controls">
                <div className="text-secondary small">
                  Page {pagination.currentPage} sur {pagination.totalPages} ({pagination.totalRecords} enregistrements)
                </div>
                <div className="buttons">
                  <button onClick={() => goToPage(1)} disabled={pagination.currentPage === 1 || loading || refreshing}>
                    Première
                  </button>
                  <button onClick={() => goToPage(pagination.currentPage - 1)} disabled={pagination.currentPage === 1 || loading || refreshing}>
                    Précédente
                  </button>
                  {[...Array(pagination.totalPages).keys()].map(num => {
                    const p = num + 1;
                    if (
                      p === 1 ||
                      p === pagination.totalPages ||
                      (p >= pagination.currentPage - 1 && p <= pagination.currentPage + 1)
                    ) {
                      return (
                        <button
                          key={p}
                          className={p === pagination.currentPage ? 'active' : ''}
                          onClick={() => goToPage(p)}
                          disabled={loading || refreshing}
                        >
                          {p}
                        </button>
                      );
                    }
                    if (p === pagination.currentPage - 2 || p === pagination.currentPage + 2) {
                      return <span key={p} style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>…</span>;
                    }
                    return null;
                  })}
                  <button onClick={() => goToPage(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages || loading || refreshing}>
                    Suivante
                  </button>
                  <button onClick={() => goToPage(pagination.totalPages)} disabled={pagination.currentPage === pagination.totalPages || loading || refreshing}>
                    Dernière
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modale Ajout / Modification */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Modifier' : 'Ajouter'} une inscription</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12">
                <Form.Label>Client</Form.Label>
                <Form.Select
                  name="idcl"
                  value={formData.idcl}
                  onChange={handleChange}
                  required
                  className="monetra-input"
                  disabled={submitting}
                >
                  <option value="">Choisir un client</option>
                  {clients.map(c => (
                    <option key={c.idcl} value={c.idcl}>{c.nom}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-12">
                <Form.Label>Formation</Form.Label>
                <Form.Select
                  name="id_formation"
                  value={formData.id_formation}
                  onChange={handleChange}
                  required
                  className="monetra-input"
                  disabled={submitting}
                >
                  <option value="">Choisir une formation</option>
                  {formations.map(f => (
                    <option key={f.id} value={f.id}>{f.titre}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-12">
                <Form.Label>Session</Form.Label>
                <Form.Select
                  name="id_session"
                  value={formData.id_session}
                  onChange={handleChange}
                  required
                  disabled={!formData.id_formation || loadingSessions || submitting}
                  className="monetra-input"
                >
                  <option value="">Choisir une session</option>
                  {loadingSessions ? (
                    <option disabled>Chargement...</option>
                  ) : sessions.length === 0 ? (
                    <option disabled>Aucune session</option>
                  ) : (
                    sessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.mois} (du {s.debut} au {s.fin})
                      </option>
                    ))
                  )}
                </Form.Select>
                {loadingSessions && <small className="text-secondary">Chargement des sessions...</small>}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)} disabled={submitting}>
              <FontAwesomeIcon icon={faTimes} /> Annuler
            </Button>
            <Button type="submit" className="monetra-btn primary" disabled={submitting}>
              {submitting ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> En cours...</>
              ) : (
                editing ? 'Modifier' : 'Créer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modale Suppression */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="monetra-modal">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirmation de suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer l'inscription ?
          <br /><span className="text-secondary small">Cette action est irréversible.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete} className="monetra-btn danger" disabled={deleting}>
            {deleting ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Suppression...</>
            ) : (
              'Supprimer'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-custom p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageInscriptions;